import React from 'react';
import { useSelector } from 'react-redux';
import { MembershipItem, MEMBERSHIP_ITEM_TYPES, MembershipItemTypeValue } from '@spike/membership-model';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service from 'model/Service';
import { RootState } from 'store';
import { resolveItemTypeFromCatalogs } from '../utils/membershipItemUtils';

interface UseMembershipItemCatalogsConfig {
    items: MembershipItem[];
    excludedItemIDs: string[];
}

export interface UseMembershipItemCatalogsReturn {
    // ─── Full catalogs (for dropdown visibility checks) ───────────────────────
    groomingCatalog: Service[];
    daycareCatalog: Service[];
    boardingCatalog: Service[];
    walkingCatalog: Service[];
    transportationCatalog: Service[];
    trainingCatalog: Service[];
    addonsCatalog: AddOnService[];
    productsCatalog: Product[];

    // ─── Available items (excludedItemIDs filtered out) ───────────────────────
    availableGrooming: Service[];
    availableDaycare: Service[];
    availableBoarding: Service[];
    availableWalking: Service[];
    availableTransportation: Service[];
    availableTraining: Service[];
    availableAddons: AddOnService[];
    availableProducts: Product[];

    // ─── Current items mapped to catalog objects (for pre-selection) ──────────
    currentGrooming: Service[];
    currentDaycare: Service[];
    currentBoarding: Service[];
    currentWalking: Service[];
    currentTransportation: Service[];
    currentTraining: Service[];
    currentAddons: AddOnService[];
    currentProducts: Product[];

    // ─── Normalized items (legacy "Primary Service" → granular type) ──────────
    normalizedItems: MembershipItem[];
}

/**
 * Shared catalog state hook used by both useMembershipItemDrawers (regular items)
 * and useMembershipDiscountedItemDrawers (discounted items). Encapsulates all
 * Redux selectors, catalog memos, normalization, and current-item lookups.
 */
export const useMembershipItemCatalogs = ({
    items,
    excludedItemIDs
}: UseMembershipItemCatalogsConfig): UseMembershipItemCatalogsReturn => {
    const servicesState = useSelector((state: RootState) => state.services);
    const productsReducer = useSelector((state: RootState) => state.products);
    const addOnServicesState = useSelector((state: RootState) => state.addOnServices);

    // ─── Catalog arrays ───────────────────────────────────────────────────────

    const groomingCatalog = React.useMemo(
        () => [
            ...(servicesState.servicesByBusinessArea?.grooming?.services || []),
            ...(servicesState.servicesByBusinessArea?.dental?.services || [])
        ],
        [servicesState.servicesByBusinessArea?.grooming, servicesState.servicesByBusinessArea?.dental]
    );

    const daycareCatalog = React.useMemo(
        () => servicesState.servicesByBusinessArea?.daycare?.services || [],
        [servicesState.servicesByBusinessArea?.daycare]
    );

    const boardingCatalog = React.useMemo(
        () => servicesState.servicesByBusinessArea?.boarding?.services || [],
        [servicesState.servicesByBusinessArea?.boarding]
    );

    const walkingCatalog = React.useMemo(
        () => servicesState.servicesByBusinessArea?.walking?.services || [],
        [servicesState.servicesByBusinessArea?.walking]
    );

    const transportationCatalog = React.useMemo(
        () => servicesState.servicesByBusinessArea?.transportation?.services || [],
        [servicesState.servicesByBusinessArea?.transportation]
    );

    const trainingCatalog = React.useMemo(
        () => servicesState.servicesByBusinessArea?.training?.services || [],
        [servicesState.servicesByBusinessArea?.training]
    );

    const addonsCatalog = React.useMemo(() => addOnServicesState.services, [addOnServicesState.services]);

    const productsCatalog = React.useMemo(
        () => (productsReducer.list.page ?? []).filter(p => p.businessProduct != null),
        [productsReducer.list.page]
    );

    // ─── Available items (excludedItemIDs filtered out) ────────────────────────

    const availableGrooming = React.useMemo(
        () => groomingCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [groomingCatalog, excludedItemIDs]
    );

    const availableDaycare = React.useMemo(
        () => daycareCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [daycareCatalog, excludedItemIDs]
    );

    const availableBoarding = React.useMemo(
        () => boardingCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [boardingCatalog, excludedItemIDs]
    );

    const availableWalking = React.useMemo(
        () => walkingCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [walkingCatalog, excludedItemIDs]
    );

    const availableTransportation = React.useMemo(
        () => transportationCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [transportationCatalog, excludedItemIDs]
    );

    const availableTraining = React.useMemo(
        () => trainingCatalog.filter(s => !excludedItemIDs.includes(String(s.id))),
        [trainingCatalog, excludedItemIDs]
    );

    const availableAddons = React.useMemo(
        () => addonsCatalog.filter(a => !excludedItemIDs.includes(String(a.id))),
        [addonsCatalog, excludedItemIDs]
    );

    const availableProducts = React.useMemo(
        () => productsCatalog.filter(p => !excludedItemIDs.includes(String(p.businessProduct?.id ?? p.id))),
        [productsCatalog, excludedItemIDs]
    );

    // ─── Normalized items: resolve legacy "Primary Service" → granular types ──

    const serviceCatalogsForNormalization = React.useMemo<Partial<Record<MembershipItemTypeValue, Service[]>>>(
        () => ({
            [MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE]: groomingCatalog,
            [MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE]: daycareCatalog,
            [MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE]: boardingCatalog,
            [MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE]: walkingCatalog,
            [MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE]: transportationCatalog,
            [MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE]: trainingCatalog
        }),
        [groomingCatalog, daycareCatalog, boardingCatalog, walkingCatalog, transportationCatalog, trainingCatalog]
    );

    const normalizedItems = React.useMemo(
        () =>
            items.map(item => ({
                ...item,
                itemType: resolveItemTypeFromCatalogs(item, serviceCatalogsForNormalization)
            })),
        [items, serviceCatalogsForNormalization]
    );

    // ─── Current items mapped back to catalog objects (for pre-selection) ──────

    const currentGrooming = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE)
                .flatMap(i => {
                    const found = groomingCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, groomingCatalog]
    );

    const currentDaycare = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE)
                .flatMap(i => {
                    const found = daycareCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, daycareCatalog]
    );

    const currentBoarding = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE)
                .flatMap(i => {
                    const found = boardingCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, boardingCatalog]
    );

    const currentWalking = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE)
                .flatMap(i => {
                    const found = walkingCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, walkingCatalog]
    );

    const currentTransportation = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE)
                .flatMap(i => {
                    const found = transportationCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, transportationCatalog]
    );

    const currentTraining = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE)
                .flatMap(i => {
                    const found = trainingCatalog.find(s => String(s.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, trainingCatalog]
    );

    const currentAddons = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.ADD_ON)
                .flatMap(i => {
                    const found = addonsCatalog.find(a => String(a.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, addonsCatalog]
    );

    const currentProducts = React.useMemo(
        () =>
            normalizedItems
                .filter(i => i.itemType === MEMBERSHIP_ITEM_TYPES.PRODUCT)
                .flatMap(i => {
                    const found = productsCatalog.find(p => String(p.businessProduct?.id ?? p.id) === String(i.itemID));
                    return found ? [found] : [];
                }),
        [normalizedItems, productsCatalog]
    );

    return {
        groomingCatalog,
        daycareCatalog,
        boardingCatalog,
        walkingCatalog,
        transportationCatalog,
        trainingCatalog,
        addonsCatalog,
        productsCatalog,
        availableGrooming,
        availableDaycare,
        availableBoarding,
        availableWalking,
        availableTransportation,
        availableTraining,
        availableAddons,
        availableProducts,
        currentGrooming,
        currentDaycare,
        currentBoarding,
        currentWalking,
        currentTransportation,
        currentTraining,
        currentAddons,
        currentProducts,
        normalizedItems
    };
};
